import { CustomSearchParams } from "@/components/common/deep-link/deep-link-encoding";
import { useUpdateProjectMetadata } from "@/components/common/project-provider/use-project-metadata";
import { userControlledFeatures } from "@/store/features/features";
import { AppDispatch } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  ToastReturn,
  useNonExhaustiveEffect,
} from "@faro-lotv/app-component-toolbox";
import { useToast } from "@faro-lotv/flat-ui";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { useEffect } from "react";
import {
  NavigateFunction,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { selectEditMode } from "../store/user-edits/data-preparation-user-edit-selectors";
import {
  disableEditMode,
  setEditMode,
} from "../store/user-edits/data-preparation-user-edit-slice";
import { DecodedDeepLinkData } from "./deep-link-data-decoded";
import { isEncodedDeepLinkData } from "./deep-link-data-encoded";
import { decodeDeepLinkData } from "./deep-link-decode";
import { encodeEditMode } from "./deep-link-encode";

/** Search param keys which are used for other parts of the application. */
const IGNORE_KEYS = [
  "revisionId",
  CustomSearchParams.showRegisterSuccess,
  CustomSearchParams.showPublishSuccess,
  CustomSearchParams.enableDev,
];

/** Search param keys which are not removed from the URL upon loading. */
const PERSIST_KEYS = ["edit"];

/**
 * Handle deep links in the data preparation tool.
 *
 * Deep links are used to share a specific view with another user.
 * They are encoded in the URL search params and removed upon page load.
 */
export function useDataPreparationDeepLink(): void {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { openToast } = useToast();
  const [searchParams] = useSearchParams();

  useNonExhaustiveEffect(() => {
    const deepLinkData = extractDeepLinkData(searchParams, openToast);
    removeDeepLinkFromUrl(navigate, searchParams, deepLinkData);
    initializeStateFromDeepLink({
      dispatch,
      deepLinkData,
    });
  }, []);

  // Always represent the current edit mode in the URL
  useSyncUrlWithEditMode();
}

/**
 * Add the currently active edit mode as a search param to the URL.
 *
 * This simplifies sharing of the current state and allows the definition of Intercom tours for the edit mode.
 */
function useSyncUrlWithEditMode(): void {
  const dispatch = useAppDispatch();
  const { openToast } = useToast();
  const { projectApiClient } = useApiClientContext();
  const hasLoadedPermissions = useUpdateProjectMetadata(
    projectApiClient.projectId,
  );
  const hasWritePermission = useAppSelector(selectHasWritePermission);
  const editMode = useAppSelector(selectEditMode);
  const navigate = useNavigate();

  useNonExhaustiveEffect(() => {
    if (hasLoadedPermissions && editMode && !hasWritePermission) {
      openToast({
        title:
          "You do not have permission to edit, please contact the project administrator.",
        variant: "warning",
      });

      dispatch(disableEditMode());
    }
  }, [hasLoadedPermissions, hasWritePermission, editMode]);

  useEffect(() => {
    const params = new URL(window.location.href).searchParams;

    if (editMode) {
      params.set("edit", encodeEditMode(editMode));
    } else {
      params.delete("edit");
    }

    navigate(
      {
        search: params.toString(),
      },
      {
        replace: true,
      },
    );
  }, [editMode, navigate]);
}

type InitializeStateFromDeepLinkParams = {
  /** The dispatch function to update the store.*/
  dispatch: AppDispatch;
  /** The data encoded in the deep link. */
  deepLinkData: DecodedDeepLinkData;
};

/**
 * Initialize the store state based on the data encoded in the deep link.
 */
function initializeStateFromDeepLink({
  dispatch,
  deepLinkData,
}: InitializeStateFromDeepLinkParams): void {
  if (deepLinkData.edit) {
    dispatch(setEditMode(deepLinkData.edit));
  }
}

/**
 * @param searchParams The search params of the current URL.
 * @param openToast Function to show a toast message, for error handling.
 * @returns The data encoded in the deep link.
 */
function extractDeepLinkData(
  searchParams: URLSearchParams,
  openToast: ToastReturn["openToast"],
): DecodedDeepLinkData {
  const data: Record<string, string> = {};

  for (const [key, value] of searchParams.entries()) {
    if (!isDeepLinkKey(key)) continue;

    data[key] = decodeURIComponent(value);
  }

  if (!isEncodedDeepLinkData(data)) {
    openToast({
      title: "Invalid deep link",
      message: "Your view might not match the shared one.",
      variant: "warning",
    });
    return {};
  }

  return decodeDeepLinkData(data);
}

/**
 * @param navigate The function to navigate to a new URL.
 * @param searchParams The search params of the current URL.
 * @param deepLinkData The data encoded in the deep link.
 */
function removeDeepLinkFromUrl(
  navigate: NavigateFunction,
  searchParams: URLSearchParams,
  deepLinkData: DecodedDeepLinkData,
): void {
  const deepLinkKeys = Object.keys(deepLinkData);

  if (deepLinkKeys.length === 0) return;

  const otherParams = new URLSearchParams();

  for (const [key, value] of searchParams.entries()) {
    if (!deepLinkKeys.includes(key) || PERSIST_KEYS.includes(key)) {
      otherParams.append(key, value);
    }
  }

  navigate(
    {
      search: `?${otherParams.toString()}`,
    },
    {
      replace: true,
    },
  );
}

/**
 * @param key the search param key to check.
 * @returns `true` if the search param key should be considered part of the deep link data.
 */
function isDeepLinkKey(key: string): boolean {
  return (
    !IGNORE_KEYS.includes(key) &&
    !userControlledFeatures().some(
      (feature) => feature.toLowerCase() === key.toLowerCase(),
    )
  );
}
